/**
 * BannerListContainer
 * --
 */
import React from 'react';
import BannerListPresenter from './BannerListPresenter';
import {
  useCreateBanner,
  useDeleteBanner,
  useGetBanners,
  useUpdateBanner,
  bannerQueryKeys,
  useUpsertBanner,
} from 'hooks/BannerHooks';
import { useGetGrades } from 'hooks/GradeHooks';
import MessageAlert from 'utils';
import { useQueryClient } from '@tanstack/react-query';
import { FileManager } from 'utils/FileManager';
import dayjs from 'dayjs';

const INITIAL_VALUE = {
  banner_name: '',
  is_show: 'Y',
  banner_start_date: dayjs().startOf('day').unix(),
  banner_end_date: dayjs().endOf('day').unix(),
  banner_url: '',
  render_url: '',
  is_new_window: 'N',
  grade_show: null,
  content_type: 'IMG',
  banner_type: 'PC',
  order: null,
};

const BannerListContainer = () => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== Mutate ===== */
  const queryClient = useQueryClient();
  const { upsertBanner } = useUpsertBanner();
  const { deleteBanner } = useDeleteBanner();
  const { createBanner } = useCreateBanner();
  const { updateBanner } = useUpdateBanner();

  /* ===== Data ===== */
  const queryOption = React.useMemo(() => {
    return {
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) {
          return [];
        }

        return data;
      },
      initialData: [],
    };
  }, []);
  const { data: Banners } = useGetBanners(null, queryOption);
  const { data: Grades } = useGetGrades(null, queryOption);

  /* ===== STATE ===== */
  const [inputValue, setInputValue] = React.useState(INITIAL_VALUE);
  const [isLoading, setIsLoading] = React.useState(false);
  const [modalType, setModalType] = React.useState(null);
  const [dataList, setDataList] = React.useState([]);

  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /**
   *
   * @param {string} t
   * @param {string | number} v
   */
  const handleChange = (t, v) => {
    setInputValue((prev) => ({
      ...prev,
      [t]: v,
    }));
  };

  /**
   * 모달 핸들러
   * --
   * @param {string} t
   * @param {object | null | array} data
   */
  const handleModal = (t = 'close', data = null) => {
    const type = t.toLocaleUpperCase();

    setIsLoading(true);

    switch (type) {
      case 'CREATE':
        setInputValue(INITIAL_VALUE);

        setTimeout(() => {
          setModalType(type);
          setIsLoading(false);
        }, 1000);
        break;
      case 'UPDATE':
      case 'DETAIL':
        setInputValue(data);

        setTimeout(() => {
          setModalType(type);
          setIsLoading(false);
        }, 1000);
        break;
      case 'SORT':
        setDataList(data);
        setTimeout(() => {
          setModalType(type);
          setIsLoading(false);
        }, 1000);
        break;
      case 'CLOSE':
      default:
        setInputValue(INITIAL_VALUE);
        setModalType(null);
        setIsLoading(false);
        break;
    }
  };

  /**
   * 배너 등록 유효성 검사
   * --
   */
  const handleValidateCheck = () => {
    const {
      banner_name,
      banner_start_date,
      banner_end_date,
      banner_url,
      banner_type,
    } = inputValue;

    if (!banner_name) {
      MessageAlert.info('배너명을 입력해주세요!');
      return false;
    }

    if (!banner_start_date || !banner_end_date) {
      MessageAlert.info('배너 노출기간을 선택해주세요!');
      return false;
    }

    if (!banner_url) {
      MessageAlert.info('배너 파일을 등록해주세요!');
      return false;
    }

    if (typeof banner_url !== 'string') {
      // 배너 타입이 PC가 아닐 경우 비디오 파일 등록 제한
      if (banner_type !== 'PC' && banner_url.type.includes('video')) {
        MessageAlert.info(
          '모바일이나 키오스크에서는 이미지파일만 등록 가능합니다!'
        );
        return false;
      }
    }

    return true;
  };

  /**
   * 배너 등록/수정/삭제
   * --
   * @param {string} t
   * @param {array} arr
   */
  const handleSubmit = async (t, arr = []) => {
    const submitType = t.toLocaleUpperCase();

    const confirm = (msg = '') => window.confirm(msg);

    try {
      setIsLoading(true);

      const { banner_url, banner_type, grade_show, content_type } = inputValue;

      const filterBanner = BannerList.filter(
        (banner) => banner.banner_type === banner_type
      );

      let res;
      let newList;
      switch (submitType) {
        case 'CREATE': // 배너 추가
          if (!handleValidateCheck()) return;

          if (!confirm('배너를 등록하시겠습니까?')) return;

          const upload = await FileManager.upload('banner', banner_url);

          if (upload.status !== 200) {
            MessageAlert.info('파일 업로드에 실패하였습니다.');
            return;
          }

          const createData = {
            ...inputValue,
            banner_url: upload.data.file_name,
            content_type: banner_url.type.includes('video') ? 'VIDEO' : 'IMG',
            order: filterBanner.length + 1,
            grade_show: !grade_show
              ? null
              : grade_show.map((item) =>
                  typeof item === 'number' ? { grade_id: item } : item
                ),
          };

          res = await createBanner(createData);

          if (res.status !== 200) {
            MessageAlert.info('배너 등록에 실패하였습니다.');
            return;
          }

          MessageAlert.success('배너가 성공적으로 등록되었습니다.');
          break;

        case 'UPDATE': // 배너 수정
          if (!handleValidateCheck()) return;

          if (!confirm('배너를 수정하시겠습니까?')) return;

          let bannerURL = banner_url;
          let contentType = content_type;
          if (typeof bannerURL !== 'string') {
            const updateUpload = await FileManager.upload('banner', bannerURL);

            if (updateUpload.status !== 200) {
              MessageAlert.info('파일 업로드에 실패하였습니다.');
              return;
            }

            contentType = bannerURL?.type?.includes('video') ? 'VIDEO' : 'IMG';
            bannerURL = updateUpload?.data?.file_name;
          }

          const updateData = {
            ...inputValue,
            banner_url: bannerURL,
            content_type: contentType,
            grade_show: !grade_show
              ? null
              : grade_show.map((item) =>
                  typeof item === 'number' ? { grade_id: item } : item
                ),
          };

          res = await updateBanner(updateData);

          if (res.status !== 200) {
            MessageAlert.info('배너 수정에 실패하였습니다.');
            return;
          }

          MessageAlert.success('배너가 성공적으로 수정되었습니다.');
          break;

        case 'DELETE': // 배너 삭제
          if (!confirm('배너를 정말 삭제하시겠습니까?')) return;

          res = await deleteBanner(inputValue.banner_id);

          if (res.status !== 200) {
            MessageAlert.info('배너 삭제에 실패하였습니다.');
            return;
          }

          const removeBanners = filterBanner.filter(
            (banner) => banner.banner_id !== inputValue.banner_id
          );

          newList = removeBanners.map((banner, index) => ({
            ...banner,
            order: index + 1,
          }));
          await upsertBanner(newList);

          MessageAlert.success('배너가 성공적으로 삭제되었습니다.');
          break;

        case 'SORT': // 순서 변경
          if (!confirm('배너순서를 변경하시겠습니까?')) return;

          const newArr = arr.map((item, index) => ({
            ...item,
            order: index + 1,
          }));

          res = await upsertBanner(newArr);

          if (res.status !== 200) {
            MessageAlert.info('순서변경에 실패하였습니다.');
            return;
          }

          MessageAlert.success('순서가 성공적으로 변경되었습니다.');
          break;

        default:
          MessageAlert.info('배너 관리 실패!');
          break;
      }

      // NOTE: 데이터 최신화
      const queryKey = bannerQueryKeys.getBanners(null);
      queryClient.invalidateQueries({ queryKey: queryKey.queryKey });

      setModalType(null);
    } catch (err) {
      console.error('[handleSubmit] Error!! ', err);
      MessageAlert.error(
        `배너 ${
          submitType === 'CREATE'
            ? '생성'
            : submitType === 'UPDATE'
            ? '수정'
            : '삭제'
        }에 실패하였습니다!`
      );
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  };

  /* ===== HOOKS ===== */
  /**
   * 배너 데이터 정제
   * --
   */
  const BannerList = React.useMemo(() => {
    if (!Banners) return [];

    const newData = Banners.map((banner) => ({
      ...banner,
      grade_show: !banner.grade_show
        ? null
        : banner.grade_show.map((show) => {
            const findGrade = Grades.find(
              (grade) => grade.grade_id === show.grade_id
            );

            return {
              ...show,
              ...findGrade,
            };
          }),
    }));

    const sortData = newData.sort((a, b) => b.created_at - a.created_at);

    return sortData;
  }, [Banners, Grades]);

  /* ===== RENDER ===== */
  return (
    <>
      <BannerListPresenter
        banners={BannerList}
        grades={Grades}
        inputValue={inputValue}
        isLoading={isLoading}
        onChange={handleChange}
        onSubmit={handleSubmit}
        setIsLoading={setIsLoading}
        modalType={modalType}
        onModal={handleModal}
        dataList={dataList}
        setDataList={setDataList}
      />
    </>
  );
};

export default BannerListContainer;
